import { ENVIRONMENT, EnvironmentValue } from 'constants/application/environment'
import type { EmbedSessionProvider } from 'utils/embedSession/embedSession'

/**
 * Which CRM may frame which embed, mirroring the frame-ancestors of firebase.json: Propstack frames the Propstack
 * surfaces, onOffice enterprise (live and beta, the provider sandbox included) frames the onOffice session.
 */
const PRODUCTION_ORIGINS: Record<EmbedSessionProvider, string[]> = {
  propstack: ['https://crm.propstack.de'],
  onoffice: ['https://crm.onoffice.de', 'https://crm-beta.onoffice.de'],
}
const NON_PRODUCTION_ORIGINS: Record<EmbedSessionProvider, string[]> = {
  propstack: ['https://development.propstack.de'],
  onoffice: [],
}
const LOCALHOST_ORIGIN_PATTERN = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/

export interface EmbedFrameContext {
  /** The CRM the embed runs for: decides which parent origins are allowed. */
  provider: EmbedSessionProvider
  isFramed: boolean
  ancestorOrigins: readonly string[]
  referrer: string
  ownOrigin: string
}

/** Thin DOM glue — kept separate so the decision logic stays a pure, testable function. */
export const readEmbedFrameContext = (provider: EmbedSessionProvider): EmbedFrameContext => ({
  provider,
  isFramed: window.self !== window.top,
  ancestorOrigins: window.location.ancestorOrigins ? Array.from(window.location.ancestorOrigins) : [],
  referrer: document.referrer,
  ownOrigin: window.location.origin,
})

const isAllowedParentOrigin = (
  origin: string,
  ownOrigin: string,
  environment: string,
  provider: EmbedSessionProvider
): boolean => {
  if (origin === ownOrigin || PRODUCTION_ORIGINS[provider].includes(origin)) return true
  if (environment === EnvironmentValue.PRODUCTION) return false
  return NON_PRODUCTION_ORIGINS[provider].includes(origin) || LOCALHOST_ORIGIN_PATTERN.test(origin)
}

const referrerOrigin = (referrer: string): string | null => {
  try {
    return new URL(referrer).origin
  } catch {
    return null
  }
}

/**
 * Defense-in-depth behind the frame-ancestors CSP header: returns the first embedding ancestor
 * origin that is not allowlisted, or null when framing is allowed. Allows when no signal is
 * available (Firefox has no ancestorOrigins and may carry no referrer) — the CSP header is the
 * actual enforcement, this check only powers the in-app error screen and telemetry.
 */
export const getBlockedEmbedAncestorOrigin = (context: EmbedFrameContext, environment: string = ENVIRONMENT): string | null => {
  if (!context.isFramed) return null
  if (context.ancestorOrigins.length > 0) {
    return context.ancestorOrigins.find(
      (origin) => !isAllowedParentOrigin(origin, context.ownOrigin, environment, context.provider)
    ) ?? null
  }
  const origin = referrerOrigin(context.referrer)
  if (origin === null) return null
  return isAllowedParentOrigin(origin, context.ownOrigin, environment, context.provider) ? null : origin
}

export type EmbedParentOriginDenialReason = 'not_framed' | 'no_signal' | 'not_allowlisted'

export interface EmbedParentOriginResolution {
  origin: string | null
  denialReason: EmbedParentOriginDenialReason | null
}

/**
 * Resolves the allowlisted parent origin to use as an outbound postMessage targetOrigin
 * (ISSUE-3101). Unlike getBlockedEmbedAncestorOrigin — which fails open because the
 * frame-ancestors CSP is the real enforcement — this fails closed: posting requires a
 * concrete targetOrigin, so no signal or a non-allowlisted parent yields null. The denial
 * reason feeds telemetry: 'no_signal' is the Firefox case (no ancestorOrigins) with a
 * suppressed referrer, where the Close affordance would otherwise disappear untraceably.
 */
export const resolveEmbedParentOrigin = (context: EmbedFrameContext, environment: string = ENVIRONMENT): EmbedParentOriginResolution => {
  if (!context.isFramed) return { origin: null, denialReason: 'not_framed' }
  const origin = context.ancestorOrigins.length > 0 ? context.ancestorOrigins[0] : referrerOrigin(context.referrer)
  if (origin === null) return { origin: null, denialReason: 'no_signal' }
  return isAllowedParentOrigin(origin, context.ownOrigin, environment, context.provider)
    ? { origin, denialReason: null }
    : { origin: null, denialReason: 'not_allowlisted' }
}

export const getEmbedParentOrigin = (context: EmbedFrameContext, environment: string = ENVIRONMENT): string | null =>
  resolveEmbedParentOrigin(context, environment).origin
